import React, { useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { useCan, Privilege } from '@feedflo/access-control';

import Button, { Colors, Variants } from '../../atoms/Button';
import Select from '../../atoms/Select';
import TextInput from '../../atoms/TextInput';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import TypeToConfirmDialog from '../../organisms/TypeToConfirmDialog';
import { DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND } from '../../utils/dates';

const AS_USER_ROLE = { headers: { 'x-hasura-role': 'user' } };

const FW_BRANCHES_GQL = gql`
  query FWBranchQuery($showDeleted: Boolean!) {
    firmware_branch(
      where: {
        _or: [
          { deleted_at: { _is_null: true } }
          { deleted_at: { _is_null: $showDeleted } }
        ]
      }
      order_by: { name: asc }
    ) {
      id
      name
      deleted_at
      dangerous_to_deploy
      devices_aggregate(where: { deleted_at: { _is_null: true } }) {
        aggregate {
          count
        }
      }
      firmwares(order_by: { created_at: desc }) {
        id
        name
        tag
        created_at
        deleted_at
      }
    }
  }
`;

const FORK_BRANCH_MUTATION = gql`
  mutation ForkFirmwareBranch($target_branch_id: uuid!, $new_name: String!) {
    fork_firmware_branch(args: { target_branch_id: $target_branch_id, new_name: $new_name }) {
      id
      name
    }
  }
`;

const COPY_FW_TO_BRANCH_MUTATION = gql`
  mutation CopyFirmwareToBranch($source_firmware_id: uuid!, $target_branch_id: uuid!, $tag_override: String!) {
    copy_firmware_to_branch(
      args: {
        source_firmware_id: $source_firmware_id
        target_branch_id: $target_branch_id
        tag_override: $tag_override
      }
    ) {
      id
      tag
      name
      firmware_branch_id
    }
  }
`;

const SOFT_DELETE_BRANCH_MUTATION = gql`
  mutation SoftDeleteFirmwareBranch($id: uuid!, $deleted_at: bigint!) {
    update_firmware_branch_by_pk(pk_columns: { id: $id }, _set: { deleted_at: $deleted_at }) {
      id
      deleted_at
    }
  }
`;

const UNDELETE_BRANCH_MUTATION = gql`
  mutation UndeleteFirmwareBranch($id: uuid!) {
    update_firmware_branch_by_pk(pk_columns: { id: $id }, _set: { deleted_at: null }) {
      id
      deleted_at
    }
  }
`;

const SOFT_DELETE_FW_MUTATION = gql`
  mutation SoftDeleteFirmware($id: uuid!, $deleted_at: bigint!) {
    update_firmware_by_pk(pk_columns: { id: $id }, _set: { deleted_at: $deleted_at }) {
      id
      deleted_at
    }
  }
`;

const UNDELETE_FW_MUTATION = gql`
  mutation UndeleteFirmware($id: uuid!) {
    update_firmware_by_pk(pk_columns: { id: $id }, _set: { deleted_at: null }) {
      id
      deleted_at
    }
  }
`;

const CREATE_BRANCH_MUTATION = gql`
  mutation CreateFirmwareBranch($name: String!) {
    insert_firmware_branch_one(object: { name: $name, dangerous_to_deploy: false }) {
      id
      name
    }
  }
`;

export default function FirmwareBranchController() {
  // --- State hooks ---
  const [showDeleted, setShowDeleted] = useState(false);
  const [forkBranch, setForkBranch] = useState(null);
  const [forkName, setForkName] = useState('');
  const [copyFw, setCopyFw] = useState(null);
  const [copyTargetBranchId, setCopyTargetBranchId] = useState('');
  const [copyTag, setCopyTag] = useState('');
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [createBranchOpen, setCreateBranchOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [fwToDelete, setFwToDelete] = useState(null);

  // --- Atoms and other Hooks ---
  const { canAnywhere } = useCan();
  const client = useApolloClient();
  const forkDialogRef = useRef(null);
  const copyDialogRef = useRef(null);
  const createBranchDialogRef = useRef(null);
  const deleteFwDialogRef = useRef(null);

  // --- Privileges and Access Hooks ---
  const isStaff = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);

  // --- Apollo/GraphQL hooks ---
  const { loading, data, error: fetchError, refetch } = useQuery(FW_BRANCHES_GQL, {
    variables: { showDeleted: !showDeleted },
    context: AS_USER_ROLE,
  });
  const firmwareBranches = data?.firmware_branch ?? [];

  const mutationOptions = {
    context: AS_USER_ROLE,
    onCompleted: () => refetch(),
  };

  const [forkBranchMutation] = useMutation(FORK_BRANCH_MUTATION, mutationOptions);
  const [copyFwMutation] = useMutation(COPY_FW_TO_BRANCH_MUTATION, mutationOptions);
  const [softDeleteBranchMutation, { loading: deletingBranch }] = useMutation(SOFT_DELETE_BRANCH_MUTATION, mutationOptions);
  const [undeleteBranchMutation] = useMutation(UNDELETE_BRANCH_MUTATION, mutationOptions);
  const [softDeleteFwMutation] = useMutation(SOFT_DELETE_FW_MUTATION, mutationOptions);
  const [undeleteFwMutation] = useMutation(UNDELETE_FW_MUTATION, mutationOptions);
  const [createBranchMutation] = useMutation(CREATE_BRANCH_MUTATION, mutationOptions);

  // --- Function definitions ---
  const showError = (msg, err) => {
    toast.error(msg, { position: 'top-right', autoClose: 5000 });
    if (err) {
      console.error(msg, err);
      Sentry.captureException(err);
    }
  };

  const downloadFWFile = async (fw) => {
    try {
      const { error: downloadError, data: dlData } = await client.query({
        query: gql`
          query DownloadFW($fw_id: uuid!) {
            firmware(where: { id: { _eq: $fw_id } }) {
              firmware_file
            }
          }
        `,
        variables: { fw_id: fw.id },
      });
      if (downloadError) throw downloadError;

      const string = dlData.firmware[0].firmware_file.substring(2);
      const binary = new Uint8Array(string.length / 2);
      for (let i = 0; i < binary.length; i += 1) {
        binary[i] = parseInt(string.substr(i * 2, 2), 16);
      }
      saveAs(new Blob([binary], { type: 'application/octet-stream' }), `${fw.name}-${fw.tag}.bin`);
    } catch (err) {
      toast.error('Error downloading firmware', { position: 'top-right' });
      Sentry.captureException(err);
    }
  };

  const openCreateBranchDialog = () => {
    setNewBranchName('');
    setCreateBranchOpen(true);
  };

  const handleCreateBranchConfirm = async () => {
    const trimmedName = newBranchName.trim();
    if (!trimmedName) return;
    try {
      await createBranchMutation({ variables: { name: trimmedName } });
      toast.success(`Created branch "${trimmedName}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to create branch', err);
    }
    setCreateBranchOpen(false);
    setNewBranchName('');
  };

  const openForkDialog = (branch) => {
    setForkBranch(branch);
    setForkName(`${branch.name} Fork`);
  };

  const handleForkConfirm = async () => {
    if (!forkBranch || !forkName.trim()) return;
    try {
      await forkBranchMutation({ variables: { target_branch_id: forkBranch.id, new_name: forkName.trim() } });
      toast.success(`Forked "${forkBranch.name}" → "${forkName.trim()}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to fork branch', err);
    }
    setForkBranch(null);
    setForkName('');
  };

  const openCopyDialog = (fw) => {
    setCopyFw(fw);
    setCopyTag(fw.tag ?? '');
    setCopyTargetBranchId('');
    if (copyDialogRef.current) copyDialogRef.current.showModal();
  };

  const closeCopyDialogUi = () => {
    if (copyDialogRef.current) copyDialogRef.current.close();
  };

  const resetCopyState = () => {
    setCopyFw(null);
    setCopyTag('');
    setCopyTargetBranchId('');
    closeCopyDialogUi();
  };

  const handleCopyNext = () => {
    if (!copyTargetBranchId) return;
    closeCopyDialogUi();
    setCopyConfirmOpen(true);
  };

  const handleCopyConfirm = async () => {
    const sourceFw = copyFw;
    const targetId = copyTargetBranchId;
    const tagOverride = copyTag.trim();

    setCopyConfirmOpen(false);
    resetCopyState();

    if (!sourceFw?.id || !targetId) {
      showError('Failed to copy firmware', new Error('Copy session expired; please try again'));
      return;
    }

    try {
      await copyFwMutation({
        variables: {
          source_firmware_id: sourceFw.id,
          target_branch_id: targetId,
          tag_override: tagOverride,
        },
      });
      const targetBranch = firmwareBranches.find((b) => b.id === targetId);
      toast.success(`Copied firmware to "${targetBranch?.name}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to copy firmware', err);
    }
  };

  const handleSoftDeleteBranch = async (branch) => {
    try {
      const nowEpoch = Math.floor(Date.now() / 1000);
      await softDeleteBranchMutation({ variables: { id: branch.id, deleted_at: nowEpoch } });
      toast.success(`Deleted branch "${branch.name}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to delete branch', err);
    }
  };

  const handleUndeleteBranch = async (branch) => {
    try {
      await undeleteBranchMutation({ variables: { id: branch.id } });
      toast.success(`Restored branch "${branch.name}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to restore branch', err);
    }
  };

  const openDeleteFwDialog = (fw, branch, isLive) => {
    setFwToDelete({ fw, branch, isLive });
  };

  const handleSoftDeleteFwConfirm = async () => {
    const target = fwToDelete;
    setFwToDelete(null);
    if (!target?.fw?.id) return;

    try {
      const nowEpoch = Math.floor(Date.now() / 1000);
      await softDeleteFwMutation({ variables: { id: target.fw.id, deleted_at: nowEpoch } });
      toast.success(`Deleted firmware "${target.fw.name}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      showError('Failed to delete firmware', err);
    }
  };

  const handleUndeleteFw = async (fw) => {
    try {
      await undeleteFwMutation({ variables: { id: fw.id } });
    } catch (err) {
      showError('Failed to restore firmware', err);
    }
  };

  // --- Derived state ---
  const targetBranchDeviceCount =
    firmwareBranches.find((b) => b.id === copyTargetBranchId)?.devices_aggregate?.aggregate?.count ?? 0;

  const branchSelectItems = firmwareBranches
    .filter((b) => !b.deleted_at)
    .map((b) => ({ id: b.id, name: `${b.name}${b.dangerous_to_deploy ? ' ⚠ DANGEROUS' : ''}` }));

  // --- Render ---
  if (loading) return <span>Loading</span>;
  if (fetchError) return <span>{JSON.stringify(fetchError, null, 2)}</span>;

  const renderFirmwareRow = (fw, isLive, branch) => {
    const isDeleted = !!fw.deleted_at;
    return (
      <div className={`fwHistory${isDeleted ? ' fwHistory--deleted' : ''}`} key={fw.id}>
        {isLive && !isDeleted && <span className="live"> - Live - </span>}
        {isDeleted && <span className="deleted"> - Deleted - </span>}
        <span className={isDeleted ? 'fwHistory-text--strike' : ''}>
          <b> Name: </b>
          {fw.name}
          <b> Date: </b>
          {dayjs.tz(1000 * fw.created_at).format(DATE_TIME_FORMAT_MONTH_DAY_YEAR_HOUR_MINUTE_SECOND)}
          <b> Tag: </b>
          <span className="tag">{fw.tag}</span>
        </span>
        <span className="fwHistory-actions">
          <a className="download" onClick={() => downloadFWFile(fw)}>Download</a>
          {isStaff && !isDeleted && (
            <button className="fwHistory-action" onClick={() => openCopyDialog(fw)}>
              Copy to branch
            </button>
          )}
          {isStaff && (
            isDeleted ? (
              <button className="fwHistory-action" onClick={() => handleUndeleteFw(fw)}>
                Undelete
              </button>
            ) : (
              <button
                className="fwHistory-action fwHistory-action--danger"
                onClick={() => openDeleteFwDialog(fw, branch, isLive)}
              >
                Delete
              </button>
            )
          )}
        </span>
      </div>
    );
  };

  const renderBranchRow = (branch) => {
    const isDeleted = !!branch.deleted_at;
    const deviceCount = branch.devices_aggregate?.aggregate?.count ?? 0;
    const liveIndex = branch.firmwares.findIndex((fw) => !fw.deleted_at);

    return (
      <div className={`fw${isDeleted ? ' fw--deleted' : ''}`} key={branch.id}>
        <div className="fw-header">
          <span className={isDeleted ? 'fw-name--strike' : ''}>
            <h4>{branch.name}</h4>
          </span>
          {isDeleted && <span className="fw-deletedBadge">DELETED</span>}
          <p className="fw-deviceCount">Devices on Branch: {deviceCount}</p>
          {isStaff && (
            <span className="fw-actions">
              {!isDeleted && (
                <>
                  <Button
                    content="Fork"
                    variant={Variants.Pastel}
                    color={Colors.Secondary}
                    onClick={() => openForkDialog(branch)}
                  />
                  <Button
                    content="Delete Branch"
                    variant={Variants.Pastel}
                    color={Colors.Danger}
                    disabled={deviceCount > 0 || deletingBranch}
                    title={deviceCount > 0 ? 'Reassign all devices before deleting' : ''}
                    onClick={() => handleSoftDeleteBranch(branch)}
                  />
                </>
              )}
              {isDeleted && (
                <Button
                  content="Restore Branch"
                  variant={Variants.Pastel}
                  color={Colors.Secondary}
                  onClick={() => handleUndeleteBranch(branch)}
                />
              )}
            </span>
          )}
        </div>

        {branch.firmwares.length > 0 && (
          <div className="spacer">
            <div className="fwHistoryWindow customScroll">
              {branch.firmwares.map((fw, i) => renderFirmwareRow(fw, i === liveIndex, branch))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="FirmwareBranchController">
      {isStaff && (
        <div className="FirmwareBranchController-toolbar">
          <Button
            content="New Branch"
            variant={Variants.Vivid}
            color={Colors.Secondary}
            onClick={openCreateBranchDialog}
          />
          <label className="FirmwareBranchController-toggleDeleted">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            {' '}Show deleted branches
          </label>
        </div>
      )}

      <div>{firmwareBranches.map(renderBranchRow)}</div>

      {/* Create branch dialog */}
      <ConfirmationDialog
        dialogRef={createBranchDialogRef}
        open={createBranchOpen}
        confirmColor={Colors.Secondary}
        onCancel={() => { setCreateBranchOpen(false); setNewBranchName(''); }}
        onConfirm={handleCreateBranchConfirm}
        message={
          <div>
            <p>Create a new empty firmware branch.</p>
            <p>Branch name:</p>
            <TextInput
              autoFocus
              value={newBranchName}
              placeholder="Branch name..."
              onChange={(e) => setNewBranchName(e.target.value)}
            />
          </div>
        }
      />

      {/* Fork branch dialog */}
      <ConfirmationDialog
        dialogRef={forkDialogRef}
        open={!!forkBranch}
        confirmColor={Colors.Secondary}
        onCancel={() => { setForkBranch(null); setForkName(''); }}
        onConfirm={handleForkConfirm}
        message={
          <div>
            <p>Fork from: <strong>{forkBranch?.name}</strong></p>
            <p>New branch name:</p>
            <TextInput
              autoFocus
              value={forkName}
              placeholder="Branch name..."
              onChange={(e) => setForkName(e.target.value)}
            />
          </div>
        }
      />

      {/* Delete firmware confirmation */}
      <ConfirmationDialog
        dialogRef={deleteFwDialogRef}
        open={!!fwToDelete}
        confirmColor={Colors.Danger}
        onCancel={() => setFwToDelete(null)}
        onConfirm={handleSoftDeleteFwConfirm}
        message={
          <div>
            <p>
              Delete firmware <strong>{fwToDelete?.fw?.name}</strong>
              {' '}(<span className="tag">{fwToDelete?.fw?.tag}</span>)
              {' '}from branch <strong>{fwToDelete?.branch?.name}</strong>?
            </p>
            {fwToDelete?.isLive && (
              <p>
                This is the <strong>live</strong> firmware for{' '}
                {fwToDelete?.branch?.devices_aggregate?.aggregate?.count ?? 0} device(s). They will
                fall back to the previous firmware on the branch at their next check-in.
              </p>
            )}
            <p>This can be undone with Undelete.</p>
          </div>
        }
      />

      {/* Copy firmware: select target branch + tag */}
      <dialog ref={copyDialogRef} className="FirmwareBranchController-copyDialog">
        <h3>Copy Firmware to Branch</h3>
        {copyFw && (
          <>
            <p>
              Copying: <strong>{copyFw.name}</strong> (<span className="tag">{copyFw.tag}</span>)
            </p>
            <Select
              label="Target Branch"
              itemList={branchSelectItems}
              value={copyTargetBranchId}
              defaultText="Select a branch..."
              onChange={(e) => setCopyTargetBranchId(e.target.value)}
            />
            <TextInput
              label="Tag (leave blank to keep original)"
              placeholder={copyFw.tag ?? ''}
              value={copyTag}
              onChange={(e) => setCopyTag(e.target.value)}
            />
          </>
        )}
        <div className="FirmwareBranchController-copyDialog-buttons">
          <Button content="Cancel" onClick={resetCopyState} />
          <Button
            content="Next"
            variant={Variants.Vivid}
            disabled={!copyTargetBranchId}
            onClick={handleCopyNext}
          />
        </div>
      </dialog>

      {/* Type-to-confirm for copy */}
      <TypeToConfirmDialog
        open={copyConfirmOpen}
        count={targetBranchDeviceCount}
        title="Confirm Firmware Copy"
        message={`This will set the live firmware on the target branch for ${targetBranchDeviceCount} device(s). Devices will receive the new firmware on their next check-in.`}
        confirmLabel="Copy Firmware"
        onCancel={() => { setCopyConfirmOpen(false); resetCopyState(); }}
        onConfirm={handleCopyConfirm}
      />
    </div>
  );
}
